import { getRouteApi, useNavigate } from '@tanstack/react-router';
import * as React from 'react';

import { ClientsClientProvider } from '@/api/clients-client-context';
import {
  ClientsListScreen,
  DEFAULT_CLIENTS_VIEW,
  type ClientsRegistryView,
} from '@/routes/admin/clients/clients-list-screen';

// =============================================================================
// clients-list-route.tsx — Adaptateur routeur du Référentiel Clients (DBU §2.3).
// =============================================================================
// Remplace le placeholder pré-câblé en Étape 0 (route `/admin/clients`). Le BC
// client-reference (S6) AUTO-PORTE son DI seam : la route enveloppe l'écran avec le
// ClientsClientProvider local (défaut = stub) plutôt que de brancher le client dans
// le providers.tsx global (surface partagée gelée en parallélisme S5bis∥S6).
//
// L'ÉTAT DE VUE (recherche/filtres/tri/page — Uc-A-CLIENTS G2/G3/G4/G8) vit dans les
// SEARCH PARAMS d'URL (RULE_REF_006 : PAS de localStorage pour l'état de vue), comme les
// autres écrans admin. L'adaptateur mappe search params ↔ ClientsRegistryView et sérialise
// la vue en retirant les DÉFAUTS (URL propre : q vide, filtres « all », tri raisonSociale
// asc, page 1 ne sont pas écrits). Le composant de présentation reste testable hors routeur.
//
// L'écran combine la liste du référentiel (§2.3) ET la création d'une fiche cliente
// (grain SIREN, AR §3.3.3) ; l'autorité (RBAC, DE.Client*, transitions Actif↔Archive)
// reste l'API. La vue détaillée (ContactReferent, enrichissement) est ADDITIVE (S6+).

const routeApi = getRouteApi('/admin/clients');

export function ClientsListRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const search = routeApi.useSearch();

  const view: ClientsRegistryView = {
    q: search.q ?? DEFAULT_CLIENTS_VIEW.q,
    statut: search.statut ?? DEFAULT_CLIENTS_VIEW.statut,
    secteur: search.secteur ?? DEFAULT_CLIENTS_VIEW.secteur,
    sortColumn: search.tri ?? DEFAULT_CLIENTS_VIEW.sortColumn,
    sortDirection: search.sens ?? DEFAULT_CLIENTS_VIEW.sortDirection,
    page: search.page ?? DEFAULT_CLIENTS_VIEW.page,
    // Mode création (Bug #3) : révélé par le search param `nouveau` (défaut = fermé).
    creating: search.nouveau ?? DEFAULT_CLIENTS_VIEW.creating,
  };

  const onChangeView = React.useCallback(
    (next: ClientsRegistryView): void => {
      // On ne sérialise QUE les valeurs non-défaut : URL propre + rétro-compatible.
      void navigate({
        to: '/admin/clients',
        search: {
          ...(next.q.trim() !== '' ? { q: next.q } : {}),
          ...(next.statut !== 'all' ? { statut: next.statut } : {}),
          ...(next.secteur !== 'all' ? { secteur: next.secteur } : {}),
          ...(next.sortColumn !== DEFAULT_CLIENTS_VIEW.sortColumn
            ? { tri: next.sortColumn }
            : {}),
          ...(next.sortDirection !== DEFAULT_CLIENTS_VIEW.sortDirection
            ? { sens: next.sortDirection }
            : {}),
          ...(next.page > 1 ? { page: next.page } : {}),
          ...(next.creating ? { nouveau: true } : {}),
        },
      });
    },
    [navigate],
  );

  return (
    <ClientsClientProvider>
      <ClientsListScreen view={view} onChangeView={onChangeView} />
    </ClientsClientProvider>
  );
}
